import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import FloatingShapes from './FloatingShapes';

import {
  Server,
  Cloud,
  Shield,
  Code,
  Cpu,
  Globe,
  Settings,
  Bot,
  Brain,
  Database,
  Code2,
  FileCode2,
  Palette,
  MonitorSmartphone,
  Atom,
  Cable,
  ShoppingCart,
  QrCode,
  Utensils,
  Smartphone,
  LayoutDashboard,
  ShieldCheck,
  Users,
  Zap,
  Target,
  Lightbulb,
  Search,
  Layers,
  Rocket,
  HeartHandshake,
  CheckCircle2,
  ArrowRight,
  ChevronUp,
} from 'lucide-react';

const Home = () => {
  const sections = useRef([]);
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => setShowTop(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observers = [];
    sections.current.forEach((section) => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fadeInScale');
            entry.target.style.pointerEvents = 'auto';
            observer.unobserve(entry.target);
          }
        },
        { threshold: 0.1 }
      );
      if (section) {
        observer.observe(section);
        section.classList.add('opacity-0');
        section.style.transition = 'opacity 0.8s ease, transform 0.8s ease';
      }
      observers.push(observer);
    });
    return () => observers.forEach((observer) => observer.disconnect());
  }, []);

  // Native touch scroll is used — no override needed

  const icons = [
    { emoji: '💻', from: { x: '5vw',  y: '20vh' }, to: { x: '15vw', y: '10vh' }, delay: 0   },
    { emoji: '☁️', from: { x: '80vw', y: '30vh' }, to: { x: '70vw', y: '20vh' }, delay: 0.4 },
    { emoji: '🔒', from: { x: '10vw', y: '70vh' }, to: { x: '20vw', y: '60vh' }, delay: 0.8 },
    { emoji: '⚙️', from: { x: '60vw', y: '80vh' }, to: { x: '50vw', y: '70vh' }, delay: 1.2 },
    { emoji: '🤖', from: { x: '40vw', y: '50vh' }, to: { x: '60vw', y: '45vh' }, delay: 1.6 },
    { emoji: '🌐', from: { x: '30vw', y: '10vh' }, to: { x: '40vw', y: '20vh' }, delay: 2   },
  ];

  const techIcons = [
    { Icon: Server,   from: { x: '10vw', y: '15vh' }, to: { x: '20vw', y: '10vh' }, delay: 0   },
    { Icon: Cloud,    from: { x: '85vw', y: '25vh' }, to: { x: '75vw', y: '20vh' }, delay: 0.3 },
    { Icon: Shield,   from: { x: '5vw',  y: '75vh' }, to: { x: '15vw', y: '65vh' }, delay: 0.6 },
    { Icon: Code,     from: { x: '70vw', y: '85vh' }, to: { x: '60vw', y: '75vh' }, delay: 0.9 },
    { Icon: Cpu,      from: { x: '45vw', y: '55vh' }, to: { x: '55vw', y: '50vh' }, delay: 1.2 },
    { Icon: Globe,    from: { x: '25vw', y: '15vh' }, to: { x: '35vw', y: '25vh' }, delay: 1.5 },
    { Icon: Settings, from: { x: '75vw', y: '70vh' }, to: { x: '65vw', y: '60vh' }, delay: 1.8 },
    { Icon: Bot,      from: { x: '30vw', y: '40vh' }, to: { x: '40vw', y: '30vh' }, delay: 2.1 },
  ];

  const solutionRows = [
    {
      items: [
        { Icon: Code2,           label: 'Custom Software',      highlight: false },
        { Icon: ShoppingCart,    label: 'eCommerce Solutions',  highlight: true  },
        { Icon: Cloud,           label: 'Cloud Services',       highlight: false },
        { Icon: Shield,          label: 'Cybersecurity',        highlight: false },
        { Icon: Globe,           label: 'Web Applications',     highlight: false },
        { Icon: Smartphone,      label: 'Mobile Apps',          highlight: false },
      ],
      direction: 1, speed: 30, offset: '0%',
    },
    {
      items: [
        { Icon: QrCode,          label: 'QR Solutions',         highlight: false },
        { Icon: Utensils,        label: 'Restaurant Tech',      highlight: true  },
        { Icon: Database,        label: 'Database Management',  highlight: false },
        { Icon: LayoutDashboard, label: 'Admin Dashboards',     highlight: false },
        { Icon: Brain,           label: 'AI Automation',        highlight: false },
        { Icon: Settings,        label: 'IT Consulting',        highlight: true  },
      ],
      direction: -1, speed: 36, offset: '-6%',
    },
    {
      items: [
        { Icon: Code2,           label: 'API Development',      highlight: false },
        { Icon: ShoppingCart,    label: 'Payment Integration',  highlight: false },
        { Icon: Globe,           label: 'SEO & Digital Growth', highlight: true  },
        { Icon: Shield,          label: 'Data Security',        highlight: false },
        { Icon: Cloud,           label: 'DevOps & CI/CD',       highlight: false },
        { Icon: Brain,           label: 'Machine Learning',     highlight: false },
      ],
      direction: 1, speed: 24, offset: '-3%',
    },
  ];

  const teamMembers = [
    { name: 'Karan Makwana', role: 'Co-Founder & Full Stack Developer', image: '/images/my photo.jpg' },
    { name: 'Om Makwana',    role: 'Co-Founder & Solution Architect', image: 'https://omkaranfurniture.com/Owners/WhatsApp%20Image%202024-12-28%20at%2020.36.29_772a2094.jpg' },
  ];

  const hoverVariants = {
    hover: { scale: 1.05, transition: { duration: 0.3 } },
    tap:   { scale: 0.98 },
  };

  const handleMouseMove = (e) => {
    // Skip parallax on touch/mobile devices to avoid scroll jank
    if (window.matchMedia('(hover: none)').matches) return;
    sections.current.slice(1).forEach((section) => {
      if (section) {
        const rect = section.getBoundingClientRect();
        section.style.setProperty('--mouse-x', `${e.clientX - rect.left - 100}px`);
        section.style.setProperty('--mouse-y', `${e.clientY - rect.top  - 100}px`);
      }
    });
  };

  useEffect(() => {
    if (!window.matchMedia('(hover: none)').matches) {
      document.addEventListener('mousemove', handleMouseMove);
      return () => document.removeEventListener('mousemove', handleMouseMove);
    }
  }, []);

  return (
    <div className="min-h-screen bg-gray-950 text-white" onMouseMove={handleMouseMove} style={{ touchAction: 'pan-y' }}>

      {/* ══ HERO ══ */}
      <section id="home" ref={(el) => (sections.current[0] = el)}
        className="relative min-h-[850px] flex items-center justify-center text-center overflow-hidden opacity-0">
        <FloatingShapes />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-950 to-blue-900/90 z-0" />
        {icons.map((icon, i) => (
          <motion.div key={i} className="absolute text-3xl sm:text-4xl opacity-40 select-none z-0"
            style={{ left: icon.from.x, top: icon.from.y, willChange: 'transform' }}
            animate={{ left: icon.to.x, top: icon.to.y, opacity: [0.4, 0.8, 0.4], rotate: [0, 10, -10, 0] }}
            transition={{ duration: 6, delay: icon.delay, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }}>
            {icon.emoji}
          </motion.div>
        ))}
        {techIcons.map(({ Icon, from, to, delay }, i) => (
          <motion.div key={i} className="absolute text-white opacity-30 z-0"
            style={{ left: from.x, top: from.y, willChange: 'transform' }}
            animate={{ left: to.x, top: to.y, opacity: [0.3, 0.6, 0.3], rotate: [0, 15, -15, 0] }}
            transition={{ repeat: Infinity, repeatType: 'reverse', duration: 6, delay, ease: 'easeInOut' }}>
            <Icon className="w-6 h-6 sm:w-8 sm:h-8" />
          </motion.div>
        ))}
        <div className="relative z-10 px-4 sm:px-6 lg:px-24 max-w-5xl">
          <motion.h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight mb-6 cursor-pointer"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
            whileHover={{ scale: 1.02, color: '#93c5fd' }}>
            Innovate with CmCoreTech
          </motion.h1>
          <motion.p className="text-base sm:text-lg md:text-xl text-gray-100 mb-8 leading-relaxed"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
            Welcome to CmCoreTech — your partner in software development, cloud solutions, and cybersecurity.
          </motion.p>
          <motion.div className="flex flex-col sm:flex-row justify-center gap-4 mb-8"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }}>
            <motion.a href="#contact" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg shadow-xl transition duration-300"
              variants={hoverVariants} whileHover="hover" whileTap="tap">Request a Consultation</motion.a>
            <motion.a href="#solutions" className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-6 py-3 rounded-lg shadow-xl transition duration-300"
              variants={hoverVariants} whileHover="hover" whileTap="tap">View Solutions</motion.a>
          </motion.div>
        </div>
      </section>


      {/* ══ ABOUT ══ */}
      <section id="about" ref={(el) => (sections.current[1] = el)}
        className="py-16 sm:py-20 bg-gray-950 opacity-0 relative interactive-section">
        <FloatingShapes />
        <div className="container mx-auto px-4 sm:px-6 md:px-12">
          <motion.h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center text-white mb-6"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            whileHover={{ color: '#93c5fd' }}>
            Empowering <span className="text-blue-400">Digital Innovation</span>
          </motion.h2>
          <motion.p className="text-center text-gray-400 text-base sm:text-lg mb-10 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
            At <span className="text-blue-300 font-semibold">CmCoreTech</span>, we help businesses thrive with tailored software, secure cloud solutions, and intelligent automation.
          </motion.p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 sm:gap-14 items-center">
            <div className="space-y-5 sm:space-y-6">
              {[
                { Icon: Cpu,    title: 'Custom Software Development', desc: 'Robust applications designed for performance, scalability, and user experience.' },
                { Icon: Cloud,  title: 'Cloud Solutions',              desc: 'Migrate to secure, fast, and flexible cloud platforms for better efficiency.' },
                { Icon: Shield, title: 'eCommerce Solutions',          desc: 'Best solutions to showcase your products and grow your online presence.' },
                { Icon: Brain,  title: 'QR Based Solutions',           desc: 'QR scan and activate your features and benefits instantly.' },
              ].map(({ Icon, title, desc }, i) => (
                <motion.div key={i} className="flex items-start gap-3 sm:gap-4"
                  initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 + i * 0.1 }}>
                  <Icon className="text-blue-400 w-5 h-5 sm:w-6 sm:h-6 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="text-white font-semibold text-lg">{title}</h4>
                    <p className="text-gray-400 text-sm">{desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
            <motion.div className="flex justify-center"
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.3 }}>
              <img
                src="https://thumbs.dreamstime.com/b/digital-abstract-technology-background-binary-futuristic-cyberspace-concept-hi-tech-communication-innovation-152230773.jpg"
                alt="IT Solutions"
                className="rounded-2xl shadow-lg w-full max-w-md border-2 border-blue-400"
              />
            </motion.div>
          </div>
        </div>
      </section>


      {/* ══ SOLUTIONS (What We Build) ══ */}
      <section id="solutions" ref={(el) => (sections.current[2] = el)}
        className="relative py-24 overflow-hidden opacity-0 interactive-section"
        style={{ background: 'linear-gradient(135deg, #050d1a 0%, #091525 60%, #050d1a 100%)' }}>
        <FloatingShapes />
        <div className="absolute inset-0 z-0 pointer-events-none opacity-[0.06]"
          style={{ backgroundImage: 'radial-gradient(circle, #4a90d9 1px, transparent 1px)', backgroundSize: '38px 38px' }} />
        <motion.div className="absolute top-10 left-1/4 w-96 h-72 rounded-full pointer-events-none hidden sm:block"
          style={{ background: 'radial-gradient(circle, rgba(37,99,235,0.15) 0%, transparent 70%)' }}
          animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }} transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }} />
        <motion.div className="absolute bottom-10 right-1/4 w-80 h-80 rounded-full pointer-events-none hidden sm:block"
          style={{ background: 'radial-gradient(circle, rgba(6,182,212,0.1) 0%, transparent 70%)' }}
          animate={{ scale: [1, 1.3, 1], opacity: [0.4, 0.8, 0.4] }} transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut', delay: 2 }} />

        <div className="relative z-10 container mx-auto px-6 lg:px-12">
          <div className="mb-16">
            <motion.span className="inline-block text-[11px] font-bold tracking-[0.22em] uppercase px-3 py-1 rounded-md mb-5"
              style={{ background: 'rgba(20,55,110,0.5)', border: '1px solid rgba(59,130,246,0.3)', color: '#7eb8f5' }}
              initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              Solutions
            </motion.span>
            <motion.h2 className="text-4xl md:text-5xl font-extrabold text-white leading-tight mb-5"
              initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
              What We{' '}
              <motion.span className="text-blue-400 inline-block"
                animate={{ color: ['#60a5fa', '#38bdf8', '#818cf8', '#60a5fa'] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}>Build</motion.span>
            </motion.h2>
            <motion.p className="text-gray-400 text-base md:text-lg max-w-xl leading-relaxed"
              initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
              Partner with a team that understands growth, technology, and design — and delivers all three.
            </motion.p>
          </div>

          <div className="space-y-5">
            {solutionRows.map((row, rowIndex) => {
              const repeated = [...row.items, ...row.items, ...row.items];
              const scrollDist = row.direction === 1 ? '-33.33%' : '33.33%';
              return (
                <motion.div key={rowIndex} className="overflow-hidden" style={{ marginLeft: row.offset }}
                  initial={{ opacity: 0, x: row.direction === 1 ? -60 : 60 }}
                  whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: 0.1 + rowIndex * 0.15 }}>
                  <motion.div className="flex items-center gap-4 w-max"
                    animate={{ x: [0, scrollDist] }}
                    transition={{ duration: row.speed, repeat: Infinity, ease: 'linear', repeatType: 'loop' }}>
                    {repeated.map((item, colIndex) => {
                      const Icon = item.Icon;
                      return (
                        <React.Fragment key={colIndex}>
                          {colIndex > 0 && (
                            <div className="flex-shrink-0 h-px w-10 md:w-14"
                              style={{ background: 'repeating-linear-gradient(90deg,rgba(59,130,246,0.25) 0px,rgba(59,130,246,0.25) 3px,transparent 3px,transparent 9px)' }} />
                          )}
                          <motion.div
                            className="flex items-center gap-3 px-4 py-3 rounded-xl cursor-pointer select-none flex-shrink-0 relative overflow-hidden"
                            style={{
                              background: item.highlight ? 'linear-gradient(135deg,#163e72 0%,#0d2b55 100%)' : 'rgba(255,255,255,0.035)',
                              border: item.highlight ? '1px solid rgba(59,130,246,0.5)' : '1px solid rgba(255,255,255,0.07)',
                              boxShadow: item.highlight ? '0 4px 20px rgba(59,130,246,0.15)' : 'none',
                            }}
                            whileHover={{ scale: 1.08, background: 'linear-gradient(135deg,#1a4a88 0%,#102f60 100%)', borderColor: 'rgba(96,165,250,0.6)', boxShadow: '0 8px 30px rgba(59,130,246,0.25)', transition: { duration: 0.2 } }}
                            whileTap={{ scale: 0.95 }}>
                            {item.highlight && (
                              <motion.div className="absolute inset-0 pointer-events-none"
                                style={{ background: 'linear-gradient(105deg,transparent 30%,rgba(255,255,255,0.07) 50%,transparent 70%)' }}
                                animate={{ x: ['-100%', '200%'] }}
                                transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut', repeatDelay: 1.5 }} />
                            )}
                            <motion.div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                              style={{ background: item.highlight ? 'rgba(59,130,246,0.25)' : 'rgba(59,130,246,0.1)' }}
                              whileHover={{ rotate: [0, -10, 10, 0], transition: { duration: 0.4 } }}>
                              <Icon className="w-4 h-4" style={{ color: item.highlight ? '#93c5fd' : '#5b9bd5' }} />
                            </motion.div>
                            <span className="text-sm font-semibold whitespace-nowrap" style={{ color: item.highlight ? '#dbeafe' : '#7aa3c8' }}>
                              {item.label}
                            </span>
                          </motion.div>
                        </React.Fragment>
                      );
                    })}
                  </motion.div>
                </motion.div>
              );
            })}
          </div>

          <motion.div className="mt-16 flex flex-col sm:flex-row items-start sm:items-center gap-4"
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.55 }}>
            <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
              <Link to="/solutions" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold px-6 py-3 rounded-xl transition-all duration-300"
                style={{ boxShadow: '0 4px 20px rgba(37,99,235,0.4)' }}>
                Explore All Solutions
                <motion.svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"
                  animate={{ x: [0, 4, 0] }} transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </motion.svg>
              </Link>
            </motion.div>
            <span className="text-gray-500 text-sm">
              or{' '}
              <a href="#contact" className="text-blue-400 hover:text-blue-300 underline underline-offset-2">talk to our team</a>
            </span>
          </motion.div>
        </div>
      </section>


      {/* ══════════════════════════════════════════════
           WHY CHOOSE US — animated cards with stats
         ══════════════════════════════════════════════ */}
      <section ref={(el) => (sections.current[3] = el)}
        className="relative py-28 overflow-hidden opacity-0 interactive-section"
        style={{ background: 'linear-gradient(160deg, #060d1f 0%, #0b1535 50%, #060e22 100%)' }}>
        <FloatingShapes />
        <div className="absolute top-0 left-0 w-full h-px"
          style={{ background: 'linear-gradient(90deg,transparent,rgba(59,130,246,0.35),transparent)' }} />

        {/* Pulsing rings */}
        {[1, 2, 3].map((i) => (
          <motion.div key={i}
            className="absolute left-1/2 top-1/2 rounded-full pointer-events-none border hidden sm:block"
            style={{ width: `${i * 220}px`, height: `${i * 220}px`, x: '-50%', y: '-50%', borderColor: `rgba(59,130,246,${0.06 - i * 0.015})` }}
            animate={{ scale: [1, 1.08, 1], opacity: [0.4, 0.8, 0.4] }}
            transition={{ duration: 4 + i, repeat: Infinity, ease: 'easeInOut', delay: i * 0.8 }} />
        ))}

        <div className="relative z-10 container mx-auto px-6 lg:px-20">
          <div className="text-center mb-20">
            <motion.span className="inline-block text-xs font-bold tracking-[0.25em] uppercase px-4 py-1.5 rounded-full mb-6"
              style={{ background: 'rgba(37,99,235,0.12)', border: '1px solid rgba(59,130,246,0.3)', color: '#93c5fd' }}
              initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              Our Advantage
            </motion.span>
            <motion.h2 className="text-4xl md:text-5xl font-extrabold text-white mb-5"
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
              Why <span className="text-blue-400">Choose Us</span>
            </motion.h2>
            <motion.p className="text-gray-400 text-lg max-w-xl mx-auto"
              initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
              We don't just build software — we build partnerships. Here's what sets us apart.
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { Icon: ShieldCheck,    color: 'blue',   title: 'Secure & Reliable',     desc: 'Cybersecurity baked into every layer — your data stays protected at all times.',      stat: '99.9%', statLabel: 'Uptime'       },
              { Icon: Zap,            color: 'purple', title: 'Fast Delivery',          desc: 'Agile sprints and battle-tested CI/CD pipelines ship features without the wait.',     stat: '2x',    statLabel: 'Faster'       },
              { Icon: Users,          color: 'cyan',   title: 'Client-First Approach',  desc: "You're involved every step. Transparent updates, real feedback loops, real results.", stat: '98%',   statLabel: 'Satisfaction' },
              { Icon: Lightbulb,      color: 'orange', title: 'Innovation Driven',      desc: 'We stay on the cutting edge — AI, cloud-native, and modern dev stacks always.',       stat: '50+',   statLabel: 'Projects'     },
              { Icon: Target,         color: 'green',  title: 'Goal-Oriented',          desc: 'Every sprint, every feature ships with one goal: your business growth.',              stat: '100%',  statLabel: 'Focused'      },
              { Icon: HeartHandshake, color: 'pink',   title: 'Long-Term Partnership',  desc: "We don't disappear after launch — ongoing support, upgrades, and guidance.",          stat: '24/7',  statLabel: 'Support'      },
            ].map(({ Icon, color, title, desc, stat, statLabel }, i) => {
              const cm = {
                blue:   ['rgba(37,99,235,0.12)',  'rgba(59,130,246,0.25)',  '#60a5fa', 'rgba(59,130,246,0.45)' ],
                purple: ['rgba(124,58,237,0.12)', 'rgba(139,92,246,0.25)', '#c084fc', 'rgba(139,92,246,0.45)' ],
                cyan:   ['rgba(6,182,212,0.12)',  'rgba(6,182,212,0.25)',  '#22d3ee', 'rgba(6,182,212,0.45)'  ],
                orange: ['rgba(234,88,12,0.12)',  'rgba(234,88,12,0.25)',  '#fb923c', 'rgba(234,88,12,0.45)'  ],
                green:  ['rgba(22,163,74,0.12)',  'rgba(22,163,74,0.25)',  '#4ade80', 'rgba(22,163,74,0.45)'  ],
                pink:   ['rgba(219,39,119,0.12)', 'rgba(219,39,119,0.25)','#f472b6', 'rgba(219,39,119,0.45)' ],
              };
              const [bg, border, ic, hborder] = cm[color];
              return (
                <motion.div key={i}
                  className="group relative p-6 rounded-2xl overflow-hidden"
                  style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${border}` }}
                  initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  whileHover={{ background: bg, borderColor: hborder, scale: 1.03, transition: { duration: 0.25 } }}>
                  <div className="flex items-start gap-4 mb-4">
                    <motion.div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: bg, border: `1px solid ${border}` }}
                      whileHover={{ rotate: [0, -10, 10, 0], transition: { duration: 0.5 } }}>
                      <Icon className="w-6 h-6" style={{ color: ic }} />
                    </motion.div>
                    <div className="ml-auto text-right">
                      <div className="text-xl font-extrabold" style={{ color: ic }}>{stat}</div>
                      <div className="text-xs text-gray-500">{statLabel}</div>
                    </div>
                  </div>
                  <h3 className="text-white font-bold text-lg mb-2">{title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{desc}</p>
                  <motion.div className="absolute bottom-0 left-0 h-0.5 rounded-full"
                    style={{ background: `linear-gradient(90deg,transparent,${ic},transparent)` }}
                    initial={{ width: '0%' }} whileInView={{ width: '100%' }} viewport={{ once: true }}
                    transition={{ duration: 1, delay: 0.3 + i * 0.1 }} />
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>


      {/* ══════════════════════════════════════════════
           OUR PROCESS — animated step-by-step timeline
         ══════════════════════════════════════════════ */}
      <section ref={(el) => (sections.current[4] = el)}
        className="relative py-28 overflow-hidden opacity-0 interactive-section"
        style={{ background: '#000000' }}>
        <FloatingShapes />
        <div className="absolute top-0 left-0 w-full h-px"
          style={{ background: 'linear-gradient(90deg,transparent,rgba(139,92,246,0.35),transparent)' }} />

        <div className="relative z-10 container mx-auto px-6 lg:px-20">
          <div className="text-center mb-20">
            <motion.span className="inline-block text-xs font-bold tracking-[0.25em] uppercase px-4 py-1.5 rounded-full mb-6"
              style={{ background: 'rgba(124,58,237,0.12)', border: '1px solid rgba(139,92,246,0.3)', color: '#c084fc' }}
              initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              How We Work
            </motion.span>
            <motion.h2 className="text-4xl md:text-5xl font-extrabold text-white mb-5"
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
              Our <span className="text-blue-400">Process</span>
            </motion.h2>
            <motion.p className="text-gray-400 text-lg max-w-xl mx-auto"
              initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
              From your first idea to a live product — every step of the journey, done right.
            </motion.p>
          </div>

          {/* Desktop: horizontal animated timeline */}
          <div className="hidden lg:block relative mb-12">
            <div className="absolute top-10 left-0 right-0 flex items-center px-16 pointer-events-none" style={{ zIndex: 0 }}>
              <motion.div className="h-0.5 w-full rounded-full"
                style={{ background: 'linear-gradient(90deg,#3b82f6,#8b5cf6,#06b6d4,#10b981,#f59e0b,#ef4444)' }}
                initial={{ scaleX: 0, originX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }}
                transition={{ duration: 1.8, ease: 'easeInOut' }} />
            </div>

            <div className="relative grid grid-cols-6 gap-4" style={{ zIndex: 1 }}>
              {[
                { Icon: Search,         color: '#3b82f6', bg: 'rgba(37,99,235,0.15)',  border: 'rgba(59,130,246,0.4)',  title: 'Discovery',   desc: 'We dig into your vision, goals, and pain points to design the right solution.' },
                { Icon: Layers,         color: '#8b5cf6', bg: 'rgba(124,58,237,0.15)', border: 'rgba(139,92,246,0.4)', title: 'Planning',    desc: 'Roadmaps, sprints, and architecture — every detail mapped before a line of code.' },
                { Icon: Code2,          color: '#06b6d4', bg: 'rgba(6,182,212,0.15)',  border: 'rgba(6,182,212,0.4)',  title: 'Development', desc: 'Scalable, secure, high-performance software built with modern stacks.' },
                { Icon: CheckCircle2,   color: '#10b981', bg: 'rgba(16,185,129,0.15)', border: 'rgba(16,185,129,0.4)', title: 'Testing',     desc: 'Automated and manual QA to ensure flawless performance across all devices.' },
                { Icon: Rocket,         color: '#f59e0b', bg: 'rgba(245,158,11,0.15)', border: 'rgba(245,158,11,0.4)', title: 'Deployment',  desc: 'CI/CD pipelines and cloud infra for smooth, zero-downtime launches.' },
                { Icon: HeartHandshake, color: '#ef4444', bg: 'rgba(239,68,68,0.15)',  border: 'rgba(239,68,68,0.4)',  title: 'Support',     desc: 'Ongoing monitoring, updates, and a dedicated team by your side always.' },
              ].map(({ Icon, color, bg, border, title, desc }, i) => (
                <motion.div key={i} className="flex flex-col items-center text-center group"
                  initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.3 + i * 0.15 }}>
                  <motion.div className="relative w-20 h-20 rounded-2xl flex items-center justify-center mb-4 cursor-default"
                    style={{ background: bg, border: `2px solid ${border}` }}
                    whileHover={{ scale: 1.15, rotate: 5, boxShadow: `0 0 28px ${color}55`, transition: { duration: 0.3 } }}>
                    <Icon className="w-8 h-8" style={{ color }} />
                    <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center text-white font-extrabold"
                      style={{ background: color, fontSize: '10px' }}>
                      {String(i + 1).padStart(2, '0')}
                    </div>
                  </motion.div>
                  <h4 className="text-white font-bold text-sm mb-2 group-hover:text-blue-300 transition-colors">{title}</h4>
                  <p className="text-gray-500 text-xs leading-relaxed">{desc}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Mobile: vertical stacked timeline */}
          <div className="lg:hidden relative">
            <div className="absolute left-8 top-0 bottom-0 w-0.5"
              style={{ background: 'linear-gradient(to bottom,#3b82f6,#8b5cf6,#06b6d4,#10b981,#f59e0b,#ef4444)' }} />
            <div className="space-y-8 pl-20">
              {[
                { Icon: Search,         color: '#3b82f6', bg: 'rgba(37,99,235,0.15)',  border: 'rgba(59,130,246,0.4)',  title: 'Discovery',   desc: 'We dig into your vision, goals, and pain points.' },
                { Icon: Layers,         color: '#8b5cf6', bg: 'rgba(124,58,237,0.15)', border: 'rgba(139,92,246,0.4)', title: 'Planning',    desc: 'Roadmaps, sprints, and architecture defined upfront.' },
                { Icon: Code2,          color: '#06b6d4', bg: 'rgba(6,182,212,0.15)',  border: 'rgba(6,182,212,0.4)',  title: 'Development', desc: 'Clean, scalable code built with modern stacks.' },
                { Icon: CheckCircle2,   color: '#10b981', bg: 'rgba(16,185,129,0.15)', border: 'rgba(16,185,129,0.4)', title: 'Testing',     desc: 'Automated and manual QA — every edge case covered.' },
                { Icon: Rocket,         color: '#f59e0b', bg: 'rgba(245,158,11,0.15)', border: 'rgba(245,158,11,0.4)', title: 'Deployment',  desc: 'CI/CD and cloud for smooth zero-downtime launches.' },
                { Icon: HeartHandshake, color: '#ef4444', bg: 'rgba(239,68,68,0.15)',  border: 'rgba(239,68,68,0.4)',  title: 'Support',     desc: 'We stay by your side long after launch day.' },
              ].map(({ Icon, color, bg, border, title, desc }, i) => (
                <motion.div key={i} className="relative flex items-start gap-5"
                  initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}>
                  <div className="absolute -left-[52px] w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ background: bg, border: `2px solid ${border}` }}>
                    <Icon className="w-5 h-5" style={{ color }} />
                  </div>
                  <div className="p-5 rounded-2xl flex-1" style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${border}` }}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-extrabold" style={{ color }}>{String(i + 1).padStart(2, '0')}</span>
                      <h4 className="text-white font-bold text-base">{title}</h4>
                    </div>
                    <p className="text-gray-400 text-sm leading-relaxed">{desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <motion.div className="text-center mt-20"
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.5 }}>
            <p className="text-gray-400 mb-6 text-lg">Ready to start your project?</p>
            <Link to="/contact"
              className="inline-flex items-center gap-2 text-white font-bold px-8 py-4 rounded-xl hover:opacity-90 transition-all duration-300"
              style={{ background: 'linear-gradient(135deg,#2563eb,#7c3aed)' }}>
              Start the Journey <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>


      {/* ══ TECHNOLOGIES ══ */}
      <section className="relative py-24 bg-gray-950 overflow-hidden">
        <FloatingShapes />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-500/30 to-transparent" />
        <div className="relative z-10 container mx-auto px-6 lg:px-16">
          <div className="text-center mb-16">
            <motion.span className="inline-block text-xs font-bold tracking-[0.25em] uppercase px-4 py-1.5 rounded-full mb-6"
              style={{ background: 'rgba(37,99,235,0.12)', border: '1px solid rgba(59,130,246,0.25)', color: '#93c5fd' }}
              initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              Tech Stack
            </motion.span>
            <motion.h2 className="text-4xl md:text-5xl font-extrabold text-white mb-4"
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
              Technologies We <span className="text-blue-400">Use</span>
            </motion.h2>
          </div>

          {[
            { category: 'Frontend', color: 'blue',   techs: [{ name: 'React.js', emoji: '⚛️' }, { name: 'React Native', emoji: '📱' }, { name: 'JavaScript', emoji: '🟨' }, { name: 'HTML5', emoji: '🌐' }, { name: 'Tailwind CSS', emoji: '🎨' }, { name: 'Bootstrap', emoji: '🅱️' }, { name: 'NativeWind', emoji: '💨' }] },
            { category: 'Backend',  color: 'purple', techs: [{ name: '.NET', emoji: '🔷' }, { name: 'ASP.NET Core MVC', emoji: '🏗️' }, { name: 'Node.js', emoji: '🟢' }, { name: 'Java', emoji: '☕' }] },
            { category: 'Database', color: 'cyan',   techs: [{ name: 'MySQL', emoji: '🐬' }, { name: 'MongoDB', emoji: '🍃' }, { name: 'Supabase', emoji: '⚡' }, { name: 'PostgreSQL', emoji: '🐘' }] },
            { category: 'DevOps',   color: 'orange', techs: [{ name: 'Docker', emoji: '🐳' }, { name: 'Kubernetes', emoji: '☸️' }, { name: 'Minikube', emoji: '🔬' }, { name: 'AWS', emoji: '☁️' }, { name: 'Grafana', emoji: '📊' }, { name: 'Prometheus', emoji: '🔥' }, { name: 'OpenTelemetry', emoji: '📡' }, { name: 'OpenSearch', emoji: '🔍' }] },
            { category: 'Design',   color: 'pink',   techs: [{ name: 'Figma', emoji: '🎭' }] },
            { category: 'Tools',    color: 'green',  techs: [{ name: 'Git', emoji: '🌿' }, { name: 'REST APIs', emoji: '🔌' }, { name: 'System Architecture', emoji: '🏛️' }] },
          ].map((group, gi) => {
            const colorMap = {
              blue:   { badge: 'bg-blue-500/15 text-blue-300 border-blue-500/30',     card: 'rgba(37,99,235,0.12)',   cardHover: 'rgba(37,99,235,0.2)',   ring: 'rgba(59,130,246,0.35)'  },
              purple: { badge: 'bg-purple-500/15 text-purple-300 border-purple-500/30', card: 'rgba(124,58,237,0.12)', cardHover: 'rgba(124,58,237,0.2)', ring: 'rgba(139,92,246,0.35)' },
              cyan:   { badge: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30',     card: 'rgba(6,182,212,0.12)',   cardHover: 'rgba(6,182,212,0.2)',   ring: 'rgba(6,182,212,0.35)'  },
              orange: { badge: 'bg-orange-500/15 text-orange-300 border-orange-500/30', card: 'rgba(234,88,12,0.12)', cardHover: 'rgba(234,88,12,0.2)',  ring: 'rgba(234,88,12,0.35)'  },
              pink:   { badge: 'bg-pink-500/15 text-pink-300 border-pink-500/30',     card: 'rgba(219,39,119,0.12)', cardHover: 'rgba(219,39,119,0.2)', ring: 'rgba(219,39,119,0.35)' },
              green:  { badge: 'bg-green-500/15 text-green-300 border-green-500/30',  card: 'rgba(22,163,74,0.12)',  cardHover: 'rgba(22,163,74,0.2)',  ring: 'rgba(22,163,74,0.35)'  },
            };
            const c = colorMap[group.color];
            return (
              <motion.div key={gi} className="mb-10 last:mb-0"
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.6, delay: gi * 0.1 }}>
                <div className="flex items-center gap-3 mb-5">
                  <span className={`text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full border whitespace-nowrap ${c.badge}`}>{group.category}</span>
                  <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.06)' }} />
                </div>
                <div className="flex flex-wrap gap-3">
                  {group.techs.map(({ name, emoji }, ti) => (
                    <motion.div key={ti}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-xl cursor-default select-none"
                      style={{ background: c.card, border: `1px solid ${c.ring}` }}
                      initial={{ opacity: 0, scale: 0.85 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
                      transition={{ duration: 0.35, delay: gi * 0.05 + ti * 0.05 }}
                      whileHover={{ background: c.cardHover, scale: 1.07, boxShadow: `0 4px 20px ${c.ring}`, transition: { duration: 0.2 } }}
                      whileTap={{ scale: 0.96 }}>
                      <span className="text-base leading-none">{emoji}</span>
                      <span className="text-sm font-semibold text-gray-200 whitespace-nowrap">{name}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>


      {/* ══ TEAM ══ */}
      <section ref={(el) => (sections.current[7] = el)}
        className="bg-gray-950 text-white py-20 opacity-0 relative interactive-section">
        <FloatingShapes />
        <div className="container mx-auto px-6">
          <motion.h2 className="text-4xl font-bold text-center mb-14"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            whileHover={{ color: '#93c5fd' }}>
            Meet Our <span className="text-blue-400">Team</span>
          </motion.h2>
          <div className="flex flex-col md:flex-row gap-10 justify-center items-center">
            {teamMembers.map((member, i) => (
              <motion.div key={i}
                className="bg-gray-900 p-6 rounded-xl shadow-lg text-center max-w-xs w-full"
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.1 }}
                variants={hoverVariants} whileHover="hover" whileTap="tap">
                <div className="w-32 h-32 mx-auto mb-4 rounded-full overflow-hidden border-4 border-blue-400">
                  <img src={member.image} alt={member.name} className="w-full h-full object-cover" />
                </div>
                <h3 className="text-xl font-semibold">{member.name}</h3>
                <p className="text-gray-400">{member.role}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* ══ CONTACT ══ */}
      <section ref={(el) => (sections.current[8] = el)} id="contact"
        className="bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 text-white py-20 opacity-0 relative interactive-section">
        <FloatingShapes />
        <div className="container mx-auto px-6 md:px-12">
          <motion.h2 className="text-4xl font-bold text-center mb-14"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            whileHover={{ color: '#93c5fd' }}>
            Let's <span className="text-blue-400">Connect</span>
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
            <motion.div className="space-y-8 text-gray-300"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <p className="text-lg leading-relaxed">
                Whether you're starting a new project, need technical support, or just want to say hello — we'd love to hear from you.
              </p>
              <div className="space-y-6">
                {[
                  { icon: '📍', label: 'Address', lines: ['Narayan-nagar Main Road, Rajkot, Gujarat'] },
                  { icon: '📞', label: 'Phone',   lines: ['+91 81415 81103', '+91 87806 43219'] },
                  { icon: '✉️', label: 'Email',   lines: ['cmcoretech@gmail.com'] },
                ].map(({ icon, label, lines }, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="p-3 bg-blue-900/40 rounded-full text-xl">{icon}</div>
                    <div>
                      <p className="font-semibold text-white">{label}</p>
                      {lines.map((l, li) => <p key={li}>{l}</p>)}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.form method="POST" action="https://api.web3forms.com/submit"
              className="bg-gray-900/60 backdrop-blur-md rounded-xl shadow-lg p-8 space-y-6"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
              <input type="hidden" name="access_key" value="200eb3f4-eb9a-4cbe-b4f6-b017c2fce186" />
              <div>
                <label className="block text-gray-300 font-medium mb-1">Your Name</label>
                <input type="text" name="name" required placeholder="John Doe"
                  className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-gray-300 font-medium mb-1">Email Address</label>
                <input type="email" name="email" required placeholder="you@example.com"
                  className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-gray-300 font-medium mb-1">Your Message</label>
                <textarea name="message" required rows="5" placeholder="Tell us about your project..."
                  className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <motion.button type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition"
                variants={hoverVariants} whileHover="hover" whileTap="tap">
                Send Message
              </motion.button>
            </motion.form>
          </div>
        </div>
      </section>


      {/* ══ FOOTER ══ */}
      <footer className="bg-gray-950 text-gray-300 py-6 text-center">
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          whileHover={{ color: '#93c5fd' }} className="cursor-pointer">
          © 2025 CmCoreTech. All rights reserved.
        </motion.p>
        <motion.p className="mt-2 cursor-pointer" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }} whileHover={{ color: '#93c5fd' }}>
          Contact: cmcoretech@gmail.com | +91 87806 43219
        </motion.p>
      </footer>

      {/* ══ BACK TO TOP ══ */}
      <motion.button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full flex items-center justify-center shadow-lg"
        style={{ background: 'linear-gradient(135deg, #2563eb, #7c3aed)' }}
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: showTop ? 1 : 0, scale: showTop ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        whileHover={{ scale: 1.15, boxShadow: '0 0 20px rgba(99,102,241,0.6)' }}
        whileTap={{ scale: 0.9 }}
        aria-label="Back to top"
      >
        <ChevronUp className="w-5 h-5 text-white" />
      </motion.button>

    </div>
  );
};

export default Home;
